import {
  Component, EventEmitter, Output, ViewEncapsulation, OnInit, OnDestroy, inject, HostListener,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule, FormBuilder, Validators, ValidatorFn, AbstractControl, ValidationErrors,
} from '@angular/forms';
import { ActualiteService } from '../../core/services/actualite.service';
import { ToastService } from '../../core/services/toast.service';
import { AuthService } from '../../auth/auth.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';



@Component({
  selector: 'app-add-user-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe],
  templateUrl: './add-user-modal.component.html',
  styleUrls: ['./add-user-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class AddUserModalComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter<void>();
  @Output() userAdded = new EventEmitter<any>();

  private actualiteSvc = inject(ActualiteService);
  private fb = inject(FormBuilder);
  private toastService = inject(ToastService);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);

  loading = false;
  createdUser: any = null;



  form = this.fb.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
  });
  
  // EQUIPEMENTS retiré (2026-09-08) : droit masqué, bloc "Équipement" de /site déprécié -- voir
  // la note DEPRECATED en tête de site.component.html.
  private static readonly PERMISSION_KEYS = [
    'ACCUEIL', 'PREVISIONS', 'SATISFACTION', 'FIL_ACTUALITE', 'QUALITE', 'ACTUALITE',
    'STATISTIQUES', 'ETABLISSEMENT', 'KIT_COMMUNICATION', 'USER_DROIT', 'SUPERVISION',
    'APPLICATION_MOBILE', 'PREVENTION',
  ];

  get availablePermissions(): { key: string; label: string }[] {
    return AddUserModalComponent.PERMISSION_KEYS.map(key => ({
      key, label: this.translate.instant('common.permissions.' + key),
    }));
  }

  private readonly adminOnlyPermissions = new Set([
    'SUPERVISION', 'APPLICATION_MOBILE', 'ACTUALITE',
    'ACCUEIL', 'PREVISIONS', 'ETABLISSEMENT', 'KIT_COMMUNICATION'
  ]);

  /** Pages V1 sans entrée de menu dédiée une fois Waitora V2 activé (remplacées par le hub /site) */
  private readonly v1OnlyPermissions = new Set([
    'ACCUEIL', 'PREVISIONS', 'ETABLISSEMENT', 'KIT_COMMUNICATION'
  ]);

  get visiblePermissions() {
    const base = this.authService.hasPermission('ADMIN')
      ? this.availablePermissions
      : this.availablePermissions.filter(p => !this.adminOnlyPermissions.has(p.key));
    if (this.hasPermission('SUPERVISION')) {
      return base.filter(p => !this.v1OnlyPermissions.has(p.key));
    }
    return base;
  }

  selectedPermissions: string[] = [];

  ngOnInit(): void {
    document.body.classList.add('modal-open');
  }

  ngOnDestroy(): void {
    document.body.classList.remove('modal-open');
  }



  onSubmit(): void {
    if (this.form.invalid || this.loading) return;

    this.loading = true;

    const raw = this.form.getRawValue();
    const payload = {
      first_name: (raw.firstName ?? '').trim(),
      last_name: (raw.lastName ?? '').trim(),
      email: (raw.email ?? '').trim(),
      permissions: this.selectedPermissions
    };

    this.actualiteSvc.createUser(payload).subscribe({
      next: (response: any) => {
        this.loading = false;
        this.createdUser = {
          email: payload.email,
          message: response.message || 'Utilisateur créé avec succès'
        };
        this.toastService.show(
          response.title || 'Utilisateur créé',
          'success',
          5000,
          response.message || 'L\'utilisateur a été créé avec succès'
        );
        this.userAdded.emit(response);
      },
      error: (err) => {
        this.loading = false;
        this.createdUser = null;
        const title = err?.error?.title || 'Erreur';
        const message = err?.error?.message || 'Échec de la création de l\'utilisateur';
        this.toastService.show(title, 'error', 5000, message);
      },
    });
  }

  closeModal(): void {
    if (this.loading) return;
    this.createdUser = null;
    this.form.reset();
    this.selectedPermissions = [];
    this.close.emit();
  }

  @HostListener('document:keydown.escape')
  onEsc(): void {
    this.closeModal();
  }
  
  togglePermission(permission: string): void {
    const index = this.selectedPermissions.indexOf(permission);
    if (index > -1) {
      this.selectedPermissions.splice(index, 1);
    } else {
      this.selectedPermissions.push(permission);
    }
  }
  
  hasPermission(permission: string): boolean {
    return this.selectedPermissions.includes(permission);
  }
}
